/**
 * Account bootstrap: every brand-new authenticated user needs a profile
 * before anything else works (org RLS can't pass without one). Two ways to
 * get one - provision_profile() creates a fresh org, accept_team_invitation()
 * joins the org that invited them. bootstrapProfile() is the one call site
 * everything should use so that choice is made in exactly one place.
 *
 * The pending invite token rides in localStorage (same pattern as
 * attribution.ts) because it has to survive a full-page redirect: OAuth
 * bounces through the provider and back, and email confirmation bounces
 * through the user's inbox - neither preserves a query param on the app's
 * own route by itself.
 */
import { supabase } from './supabase';

const PENDING_INVITE_KEY = 'cpg_pending_invite_token';

export function setPendingInviteToken(token: string): void {
  try {
    localStorage.setItem(PENDING_INVITE_KEY, token);
  } catch {
    // Storage unavailable - the invite link still works, it just won't
    // survive a redirect-based auth flow; email/password signup on the
    // same page load is unaffected.
  }
}

function getPendingInviteToken(): string | null {
  try {
    return localStorage.getItem(PENDING_INVITE_KEY);
  } catch {
    return null;
  }
}

function clearPendingInviteToken(): void {
  try {
    localStorage.removeItem(PENDING_INVITE_KEY);
  } catch {
    // ignore
  }
}

/**
 * Call this anywhere provision_profile() used to be called directly:
 * Signup, OAuthCallback, and AppLayout's self-heal. Idempotent either way -
 * an existing profile is returned unchanged by both underlying RPCs.
 */
export async function bootstrapProfile() {
  const token = getPendingInviteToken();
  if (token) {
    const { data, error } = await supabase.rpc('accept_team_invitation', { p_token: token });
    // Whether it succeeded or the token was stale/expired/already used,
    // it's spent - don't keep retrying it against provision_profile on
    // every future load, which would silently strand the user in a fresh
    // org instead of surfacing the real error.
    clearPendingInviteToken();
    if (!error) return { data, error: null };
    console.error('Failed to accept team invitation, falling back to a new organization:', error);
  }
  return supabase.rpc('provision_profile');
}
